import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridSize } from 'types/capi/plp';

import LargeGridIcon from '~/assets/icons/LargeGrid';
import SmallGridIcon from '~/assets/icons/SmallGrid';
import gtm from '~/services/gtm.client';

import Dropdown, { DropdownOption } from './Dropdown';

type Props = {
  gridSize: GridSize;
  setGridSize: React.Dispatch<React.SetStateAction<GridSize>>;
};

const VIEW_OPTIONS: {
  Icon: ComponentType;
  labelKey: 'viewLarge' | 'viewSmall';
  value: GridSize;
}[] = [
  { Icon: LargeGridIcon, labelKey: 'viewSmall', value: 'small' },
  { Icon: SmallGridIcon, labelKey: 'viewLarge', value: 'large' }
];

const ViewOptions = ({ gridSize, setGridSize }: Props) => {
  const { t } = useTranslation('plp');

  const SelectedIcon =
    VIEW_OPTIONS.find((option) => option.value === gridSize)?.Icon ??
    LargeGridIcon;

  function onSelect(value: GridSize) {
    setGridSize(value);

    gtm.trackedButtonClick({
      buttonName: `GRID_SIZE_TOGGLE_${value.toUpperCase()}`,
      pagePosition: 'Top Bar'
    });
  }

  return (
    <Dropdown
      className="z-10 w-fit max-w-lg"
      name="view"
      selectedValue={gridSize}
      trigger={
        <>
          <SelectedIcon />
          {t('view')}
        </>
      }
      triggerClassName="flex items-center gap-2 text-sm"
    >
      {(close) =>
        VIEW_OPTIONS.map(({ labelKey, value }) => (
          <DropdownOption
            isSelected={value === gridSize}
            key={value}
            name="view"
            onClick={() => {
              onSelect(value);
              close();
            }}
            value={value}
          >
            {t(labelKey)}
          </DropdownOption>
        ))
      }
    </Dropdown>
  );
};

export default ViewOptions;
